import type { Ref } from "react"
import Accordion from "@/components/Accordion/Accordion"
import Stepper from "@/components/Stepper/Stepper"
import Toggle from "@/components/Toggle/Toggle"
import {
  exercisesFor,
  exercisesSummary,
  handCapFormat,
  roundsSummary,
  steppersFor,
  stepperHint,
  RULES_COPY,
} from "@/content/sessionRules"
import type { SessionConfig } from "@/types"
import styles from "@/styles/components/SessionRules.module.css"

/** The numbers and the optional exercises, behind two accordions. */
interface AdditionalSettingsProps {
  config: SessionConfig
  openRounds: boolean
  openExercises: boolean
  onToggleRounds: () => void
  onToggleExercises: () => void
  /** The optional `key` is what `isPending` below answers for. */
  onChange: (patch: Partial<SessionConfig>, key?: string) => void
  /** From `useFirestoreWrite`, so a toggle mid-write can say so. */
  isPending: (key: string) => boolean
  /** So the Custom tile can put the cursor on the first control in here. */
  groupRef: Ref<HTMLDivElement>
}

export default function AdditionalSettings({
  config,
  openRounds,
  openExercises,
  onToggleRounds,
  onToggleExercises,
  onChange,
  isPending,
  groupRef,
}: AdditionalSettingsProps) {
  return (
    <section className={styles.block}>
      <div className={styles.group} ref={groupRef}>
        <Accordion
          title={RULES_COPY.rounds}
          summary={roundsSummary(config)}
          open={openRounds}
          onToggle={onToggleRounds}
        >
          {steppersFor(config).map((stepper) => (
            <Stepper
              key={stepper.key}
              label={stepper.label}
              hint={stepperHint(stepper, config)}
              value={config[stepper.key]}
              min={stepper.min}
              max={stepper.max}
              format={stepper.key === "handCap" ? handCapFormat : undefined}
              onChange={(value) => onChange({ [stepper.key]: value })}
            />
          ))}
        </Accordion>

        <Accordion
          title={RULES_COPY.exercises}
          summary={exercisesSummary(config)}
          open={openExercises}
          onToggle={onToggleExercises}
        >
          {exercisesFor().map((exercise) => (
            <Toggle
              key={exercise.key}
              label={exercise.label}
              hint={exercise.hint}
              checked={config[exercise.key]}
              busy={isPending(exercise.key)}
              onChange={(checked) => onChange({ [exercise.key]: checked }, exercise.key)}
            />
          ))}
        </Accordion>
      </div>
    </section>
  )
}
